import { View, Text, Pressable, ScrollView, Platform, Image } from 'react-native';
import type { Ref } from 'react';
import { OddsTile } from './OddsTile';
import { CorrectScorePicker } from '../MatchDetailsScreen/CorrectScorePicker';
import {
  groupOverUnderValuesByLine,
  groupHandicapValuesByLine,
  getHandicapOptionDisplayLabel,
  parseHandicapLineLabel,
} from '../MatchDetailsScreen/overUnderGrouping.helper';
import {
  SCORE_PICKER_MARKET_IDS,
  OVER_UNDER_MARKET_IDS,
  MARKET_IDS,
  HANDICAP_MARKET_FAMILY,
  PREMIUM_MARKET_IDS,
  PREMIUM_MARKETS_ENABLED,
} from '../../../src/create/constants/matchDetails.constants';
import { isPremiumMarketIdHiddenOnIos } from '../../../src/create/helpers/premiumMarketsIosDefer.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getInlineMarketPickerStyles } from './InlineMarketPicker.styles';
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets';
import type { MarketOdds, MarketOddValue } from '../../../src/create/types/create.types';

const styles = getInlineMarketPickerStyles();

const MERGED_OU_IDS = new Set<number>([
  MARKET_IDS.OVER_UNDER_15,
  MARKET_IDS.OVER_UNDER_05_1ST_HALF,
  MARKET_IDS.CORNERS,
]);

function isOUMarket(market: MarketOdds): boolean {
  return MERGED_OU_IDS.has(market.id) || OVER_UNDER_MARKET_IDS.includes(market.id);
}

function isScoreMarket(market: MarketOdds): boolean {
  return SCORE_PICKER_MARKET_IDS.includes(market.id);
}

function isHandicapMerged(market: MarketOdds): boolean {
  return (
    (HANDICAP_MARKET_FAMILY.ids as readonly number[]).includes(market.id) &&
    (market.values ?? []).some((v) => v.marketId != null)
  );
}

export interface OddsRendererProps {
  market: MarketOdds;
  gameId: string;
  homeName: string;
  awayName: string;
  isSelected: (gameId: string, marketId: number, value: string) => boolean;
  onPick: (gameId: string, marketId: number, marketName: string, value: string, odd: number) => void;
  isPro?: boolean;
  onPremiumBlock?: () => void;
  /**
   * Premium market IDs the user may pick even when not Pro — e.g. markets a challenge already
   * offers, which the server accepts from non-Pro invitees. Empty/undefined keeps the Pro lock.
   */
  unlockedPremiumMarketIds?: ReadonlySet<number>;
  /** Walkthrough: outcome value whose tile should receive `spotlightTileRef` (e.g. 'Home'). */
  spotlightValue?: string | null;
  /** Walkthrough: ref attached to the tile matching `spotlightValue` so the overlay can measure it. */
  spotlightTileRef?: Ref<View>;
}

const CROWN_SOURCE = { uri: getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON) };

export function MarketOddsRenderer({
  market,
  gameId,
  homeName,
  awayName,
  isSelected,
  onPick,
  isPro = false,
  onPremiumBlock,
  unlockedPremiumMarketIds,
  spotlightValue = null,
  spotlightTileRef,
}: OddsRendererProps) {
  if (isScoreMarket(market)) {
    return (
      <CorrectScorePicker
        title={market.name}
        homeName={homeName}
        awayName={awayName}
        market={market}
        gameId={gameId}
        handleAddOtherPick={onPick}
        isOtherPickSelected={isSelected}
        inline
      />
    );
  }

  if (isOUMarket(market) && (market.values?.length ?? 0) > 0) {
    const rows = groupOverUnderValuesByLine(market.values ?? []);
    if (rows.length > 0) {
      return (
        <View style={styles.ouGrid}>
          <View style={styles.ouHeaderRow}>
            <View style={styles.ouLineSpacer} />
            <View style={styles.ouHeaderCell}>
              <Text style={styles.ouHeaderText}>OVER</Text>
            </View>
            <View style={styles.ouHeaderCell}>
              <Text style={styles.ouHeaderText}>UNDER</Text>
            </View>
          </View>
          {rows.map((row) =>
            row.values.map((v) => {
              const isOver = String(v.value ?? '').toLowerCase().includes('over');
              if (!isOver) return null;
              const underV = row.values.find((x) =>
                String(x.value ?? '').toLowerCase().includes('under')
              );
              const effectiveId = v.marketId ?? market.id;
              const underEffectiveId = underV?.marketId ?? market.id;
              if (Platform.OS === 'ios' && isPremiumMarketIdHiddenOnIos(effectiveId)) return null;
              const rowIsPremium = PREMIUM_MARKET_IDS.has(effectiveId);
              const rowIsLocked = rowIsPremium && !isPro && !unlockedPremiumMarketIds?.has(effectiveId);
              const selOver = isSelected(gameId, effectiveId, v.value);
              const selUnder = underV ? isSelected(gameId, underEffectiveId, underV.value) : false;
              return (
                <View key={row.lineLabel} style={[styles.ouDataRow, rowIsLocked && styles.rowDimmed]}>
                  <View style={[styles.ouLineCell, styles.ouLineCellRow]}>
                    {rowIsPremium && PREMIUM_MARKETS_ENABLED && (
                      <Image source={CROWN_SOURCE} style={styles.ouRowCrown} resizeMode="contain" />
                    )}
                    <Text style={styles.ouLineText}>{row.lineLabel}</Text>
                  </View>
                  <Pressable
                    style={({ pressed }) => [
                      styles.ouCell,
                      selOver && styles.ouCellSelected,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.tilePressed,
                    ]}
                    onPress={() => rowIsLocked ? onPremiumBlock?.() : onPick(gameId, effectiveId, market.name, v.value, Number(v.odd) || 0)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: selOver }}
                  >
                    <Text style={[styles.ouCellText, selOver && styles.ouCellTextSelected]}>
                      {(Number(v.odd) || 0).toFixed(2)}
                    </Text>
                  </Pressable>
                  {underV && (
                    <Pressable
                      style={({ pressed }) => [
                        styles.ouCell,
                        selUnder && styles.ouCellSelected,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.tilePressed,
                      ]}
                      onPress={() => rowIsLocked ? onPremiumBlock?.() : onPick(gameId, underEffectiveId, market.name, underV.value, Number(underV.odd) || 0)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: selUnder }}
                    >
                      <Text style={[styles.ouCellText, selUnder && styles.ouCellTextSelected]}>
                        {(Number(underV.odd) || 0).toFixed(2)}
                      </Text>
                    </Pressable>
                  )}
                </View>
              );
            })
          )}
        </View>
      );
    }
  }

  if (isHandicapMerged(market) && (market.values?.length ?? 0) > 0) {
    const rows = groupHandicapValuesByLine(market.values ?? []);
    if (rows.length > 0) {
      return (
        <View style={styles.hcapBlock}>
          {rows.map((row) => {
            const rowMarketId = row.values[0]?.marketId ?? market.id;
            if (Platform.OS === 'ios' && isPremiumMarketIdHiddenOnIos(rowMarketId)) return null;
            const rowIsPremium = PREMIUM_MARKET_IDS.has(rowMarketId);
            const rowIsLocked = rowIsPremium && !isPro && !unlockedPremiumMarketIds?.has(rowMarketId);
            return (
              <View key={row.lineLabel} style={rowIsLocked ? styles.rowDimmed : undefined}>
                <View style={styles.hcapLineRow}>
                  <Text style={styles.hcapLineTag}>LINE</Text>
                  <View style={styles.hcapLinePill}>
                    <Text style={styles.hcapLinePillText}>{row.lineLabel}</Text>
                  </View>
                  {rowIsPremium && PREMIUM_MARKETS_ENABLED && (
                    <Image source={CROWN_SOURCE} style={styles.ouRowCrown} resizeMode="contain" />
                  )}
                </View>
                <View style={styles.tilesRow}>
                  {row.values.map((v) => {
                    const effectiveId = v.marketId ?? market.id;
                    const sel = isSelected(gameId, effectiveId, v.value);
                    const line = parseHandicapLineLabel(row.lineLabel);
                    const tileLabel = line != null
                      ? getHandicapOptionDisplayLabel(line, v.value as 'Home' | 'Draw' | 'Away')
                      : v.value;
                    return (
                      <OddsTile
                        key={v.value}
                        label={tileLabel}
                        odd={Number(v.odd) || 0}
                        selected={sel}
                        onPress={() => rowIsLocked
                          ? onPremiumBlock?.()
                          : onPick(gameId, effectiveId, market.name, v.value, Number(v.odd) || 0)
                        }
                      />
                    );
                  })}
                </View>
              </View>
            );
          })}
        </View>
      );
    }
  }

  const values: MarketOddValue[] = market.values ?? [];
  if (values.length === 0) return null;

  if (values.length <= 3) {
    return (
      <View style={styles.tilesRow}>
        {values.map((v) => {
          const effectiveId = v.marketId ?? market.id;
          const sel = isSelected(gameId, effectiveId, v.value);
          return (
            <OddsTile
              key={v.value}
              tileRef={spotlightValue != null && v.value === spotlightValue ? spotlightTileRef : undefined}
              label={v.value}
              odd={Number(v.odd) || 0}
              selected={sel}
              onPress={() => onPick(gameId, effectiveId, market.name, v.value, Number(v.odd) || 0)}
            />
          );
        })}
      </View>
    );
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View style={styles.manyChipsRow}>
        {values.map((v) => {
          const effectiveId = v.marketId ?? market.id;
          const sel = isSelected(gameId, effectiveId, v.value);
          return (
            <Pressable
              key={v.value}
              style={({ pressed }) => [
                styles.manyChip,
                sel && styles.manyChipSelected,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.tilePressed,
              ]}
              onPress={() => onPick(gameId, effectiveId, market.name, v.value, Number(v.odd) || 0)}
              accessibilityRole="button"
              accessibilityState={{ selected: sel }}
            >
              <Text style={[styles.manyChipLabel, sel && styles.manyChipLabelSelected]}>
                {v.value}
              </Text>
              <Text style={[styles.manyChipOdds, sel && styles.manyChipOddsSelected]}>
                ×{(Number(v.odd) || 0).toFixed(2)}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  );
}
